import './Page.css';
import { PortalIframe } from './PortalIframe';
import { useQuestionCounts, formatCount } from '../hooks/useQuestionCounts';

const scoreWins = [
  { before: '1210', after: '1380', gain: '+170', name: 'Samir K.', detail: 'SAT · Grade 11' },
  { before: '1100', after: '1380', gain: '+280', name: 'Kavita J.', detail: 'SAT · 10 sessions' },
  { before: '22', after: '26', gain: '+4', name: 'Melissa D.', detail: "ACT · Parent's report" },
];

const testimonials = [
  {
    quote: "I went from a 1210 to a 1380 after just six weeks. Practicing daily in the question bank and reviewing every session made the difference.",
    name: 'Samir K.', role: 'Grade 11',
  },
  {
    quote: "The question bank let me practice any topic whenever I had 20 minutes. Reviewing every past session showed me exactly where I kept losing points.",
    name: 'Riya P.', role: 'IL',
  },
  {
    quote: "My son connected with his tutor for 1:1 tailored SAT sessions, understood the strategies, and went into his test feeling genuinely prepared.",
    name: 'Rajesh P.', role: 'Parent',
  },
  {
    quote: "After just a few tailored sessions, my daughter's ACT score went up by 4 points. She walked out of her final test smiling — that meant a lot to us.",
    name: 'Melissa D.', role: 'Parent',
  },
];

export default function MarketingHomePage() {
  const counts = useQuestionCounts();

  const stats = [
    { num: formatCount(counts.sat.total), label: 'SAT Practice Questions' },
    { num: formatCount(counts.act.total), label: 'ACT Practice Questions' },
    { num: '97%', label: 'Students Improve' },
    { num: '$55/hr', label: 'Live Expert Tutoring' },
  ];

  const offerings = [
    {
      icon: '📐',
      label: 'SAT Prep',
      to: '/sat-prep',
      badge: 'Most Popular',
      badgeColor: '#f5a623',
      desc: `${formatCount(counts.sat.total)} questions across all SAT Math and Reading & Writing topics. Full review mode + AI assistant.`,
      cta: 'Explore SAT Prep',
      highlight: true,
    },
    {
      icon: '📋',
      label: 'ACT Prep',
      to: '/act-prep',
      badge: 'Coming Soon',
      badgeColor: '#f59e0b',
      desc: 'ACT question bank is coming soon. Expert ACT tutors are available now for live 1:1 sessions.',
      cta: 'Get ACT Tutoring',
      highlight: false,
      comingSoon: true,
    },
    {
      icon: '⚛️',
      label: 'AP Subjects',
      to: '/ap-physics',
      badge: 'Coming Soon',
      badgeColor: '#f59e0b',
      desc: 'AP question banks are coming soon. Expert AP tutors are available now for every AP course.',
      cta: 'Get AP Tutoring',
      highlight: false,
      comingSoon: true,
    },
    {
      icon: '📚',
      label: 'Subject Tutoring',
      to: '/subject-tutoring',
      badge: null,
      badgeColor: '',
      desc: '1:1 live tutoring for Math, Science, English, History, and Honors courses — Grades 6–12.',
      cta: 'See All Subjects',
      highlight: false,
    },
    {
      icon: '✍️',
      label: 'ELA Mastery',
      to: '/english-tutoring',
      badge: null,
      badgeColor: '',
      desc: 'Advanced reading, writing, and grammar programs for Grades 5–12. College-ready confidence.',
      cta: 'Explore ELA',
      highlight: false,
    },
    {
      icon: '🎓',
      label: 'College Counseling',
      to: '/college-counseling',
      badge: null,
      badgeColor: '',
      desc: 'Essays, school lists, application strategy, and scholarship support — from freshman year to senior deadlines.',
      cta: 'Explore Counseling',
      highlight: false,
    },
  ];

  return (
    <div className="page-wrap">

      {/* ── Hero ── */}
      <div className="exam-hero">
        <div className="exam-hero-inner">
          <div className="exam-hero-text">
            <div className="exam-hero-badge">SAT · ACT · AP · Subject Tutoring</div>
            <h1>The prep platform<br />with real tutors behind it.</h1>
            <p className="exam-hero-sub">
              {formatCount(counts.grand)} practice questions for SAT and ACT. Full review mode. AI-powered explanations from Aira, our built-in chatbot.
              And expert human tutors available for every subject — live 1:1, whenever you need them.
            </p>
            <p className="exam-hero-proof">★★★★★ &nbsp; 97% of students improve · 150+ avg SAT point gain</p>
            <div className="exam-hero-btns">
              <a href="/signup" className="btn-gold">Start Practicing Free</a>
              <a href="/contact" className="btn-outline-white">Talk to an Expert →</a>
            </div>
          </div>
          <div className="exam-hero-visual" style={{ alignItems: 'center', paddingBottom: 32 }}>
            <PortalIframe src="/preview/dashboard" naturalHeight={640} displayUrl="scholar-sphere.com/dashboard" />
          </div>
        </div>
      </div>

      {/* ── Stats strip ── */}
      <div className="exam-stats-strip">
        <div className="container">
          <div className="exam-stats-inner">
            {stats.map(s => (
              <div key={s.label}>
                <div className="exam-stat-num">{s.num}</div>
                <div className="exam-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container page-body">

        {/* ── Score wins ── */}
        <div className="page-section text-center">
          <span className="section-label">Real Results</span>
          <h2 className="section-heading" style={{ marginBottom: 8 }}>Students who put in the work, win.</h2>
          <p className="section-sub" style={{ marginBottom: 32 }}>Consistent practice + expert guidance = real score gains.</p>
          <div className="score-wins">
            {scoreWins.map(w => (
              <div key={w.name} className="score-win-card">
                <div className="score-win-scores">
                  <span className="score-win-before">{w.before}</span>
                  <span className="score-win-arrow">→</span>
                  <span className="score-win-after">{w.after}</span>
                </div>
                <span className="score-win-gain">{w.gain} points</span>
                <div className="score-win-name">{w.name}</div>
                <div className="score-win-detail">{w.detail}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── SAT & ACT spotlight ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 32 }}>
            <span className="section-label">Question Bank</span>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>SAT prep that actually shows you where you stand.</h2>
            <p className="section-sub">
              Most platforms give you questions and move on. Scholar Sphere shows you exactly
              what you got wrong, why, and what to do about it — with an AI assistant and real tutors backing you up.
              ACT question bank coming soon.
            </p>
          </div>

          <div className="two-col-section" style={{ alignItems: 'stretch' }}>
            {/* SAT card */}
            <div style={{ background: 'linear-gradient(135deg, #0d2248, #1a3a6b)', borderRadius: 20, padding: 36, color: '#fff', display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--gold)', marginBottom: 10 }}>SAT Prep</div>
              <h3 style={{ fontSize: 26, fontWeight: 900, color: '#fff', marginBottom: 10, lineHeight: 1.2 }}>{formatCount(counts.sat.total)} SAT questions.<br />Full review. AI explanations.</h3>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, marginBottom: 20, flex: 1 }}>
                Every SAT Math and Reading & Writing topic covered. Practice by subtopic,
                review every session in detail, and get step-by-step AI explanations on every answer.
              </p>
              <ul style={{ listStyle: 'none', marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['SAT Math: Algebra, Advanced Math, Data & Problem Solving, Geometry', 'SAT Reading & Writing: all 4 skill domains', 'Full review mode after every session', 'AI assistant on every question'].map(f => (
                  <li key={f} style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', paddingLeft: 18, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: 'var(--gold)' }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <a href="/signup" className="btn-gold">Start Free</a>
                <a href="/sat-prep" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 600, alignSelf: 'center', textDecoration: 'underline' }}>Learn more →</a>
              </div>
            </div>

            {/* ACT card — coming soon */}
            <div style={{ background: 'linear-gradient(135deg, #1a1a2e, #2d2d44)', borderRadius: 20, padding: 36, color: '#fff', display: 'flex', flexDirection: 'column', gap: 0, opacity: 0.85 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8' }}>ACT Prep</div>
                <div style={{ background: '#f59e0b', color: '#78350f', fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 20 }}>COMING SOON</div>
              </div>
              <h3 style={{ fontSize: 26, fontWeight: 900, color: 'rgba(255,255,255,0.7)', marginBottom: 10, lineHeight: 1.2 }}>ACT question bank<br />coming soon.</h3>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.65, marginBottom: 20, flex: 1 }}>
                We're building a comprehensive ACT question bank. In the meantime, our expert ACT tutors are available for live 1:1 sessions.
              </p>
              <ul style={{ listStyle: 'none', marginBottom: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['English, Math, Reading & Science coverage', 'Expert live 1:1 ACT tutoring available now', 'Full question bank + AI explanations — coming soon'].map(f => (
                  <li key={f} style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', paddingLeft: 18, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#94a3b8' }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <a href="/act-prep" className="btn-gold">Get ACT Tutoring</a>
                <a href="/contact" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: 600, alignSelf: 'center', textDecoration: 'underline' }}>Talk to an expert →</a>
              </div>
            </div>
          </div>
        </div>

        {/* ── Platform preview ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 40 }}>
            <span className="section-label">Inside the Platform</span>
            <h2 className="section-heading" style={{ marginBottom: 8, marginTop: 6 }}>
              See exactly what you're getting.
            </h2>
            <p className="section-sub">
              Real screenshots of the question bank, analytics dashboard, and practice interface.
              No stock photos — this is the actual product.
            </p>
          </div>

          {/* Dashboard + Analytics side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>📊 Your Dashboard</p>
              <p style={{ fontSize: 13, color: 'var(--text-gray)', marginBottom: 12 }}>See your progress across every subject, pick up where you left off, and know exactly where to focus.</p>
              <PortalIframe src="/preview/dashboard" naturalHeight={600} displayUrl="scholar-sphere.com/dashboard" />
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>✏️ Practice Mode</p>
              <p style={{ fontSize: 13, color: 'var(--text-gray)', marginBottom: 12 }}>Clean question interface with a timer, AI assistant, and bookmark on every single question.</p>
              <PortalIframe src="/preview/practice" naturalHeight={580} displayUrl="scholar-sphere.com/practice" />
            </div>
          </div>

          <div className="text-center" style={{ marginTop: 32 }}>
            <a href="/signup" className="btn-gold" style={{ fontSize: 15, padding: '14px 36px', display: 'inline-block' }}>
              Try It Free — No Card Required
            </a>
          </div>
        </div>

        {/* ── All offerings ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 32 }}>
            <span className="section-label">All Programs</span>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Everything a student needs. One place.</h2>
            <p className="section-sub">From standardized tests to college essays — we cover it all.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {offerings.map(o => (
              <div key={o.label} style={{
                background: o.highlight ? '#fff' : 'var(--off-white)',
                border: `${o.highlight ? '2px' : '1px'} solid ${o.highlight ? 'var(--navy)' : 'var(--border)'}`,
                borderRadius: 16, padding: 24,
                display: 'flex', flexDirection: 'column', gap: 0,
                opacity: (o as any).comingSoon ? 0.8 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 28 }}>{o.icon}</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)' }}>{o.label}</div>
                    {o.badge && (
                      <div style={{ display: 'inline-block', background: o.badgeColor, color: (o as any).comingSoon ? '#78350f' : (o.label === 'SAT Prep' ? 'var(--navy-dark)' : '#fff'), fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>{o.badge}</div>
                    )}
                  </div>
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-gray)', lineHeight: 1.6, flex: 1, marginBottom: 16 }}>{o.desc}</p>
                <a href={o.to} style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', textDecoration: 'underline' }}>{o.cta} →</a>
              </div>
            ))}
          </div>
        </div>

        {/* ── Why tutoring + platform together ── */}
        <div className="page-section">
          <div className="two-col-section" style={{ alignItems: 'center' }}>
            <div>
              <span className="section-label">The Scholar Sphere Difference</span>
              <h2 className="section-heading" style={{ marginTop: 8, marginBottom: 14 }}>Software alone won't get you to 1500.</h2>
              <p className="page-p">
                Other platforms give you questions and leave you alone. Scholar Sphere combines
                a {formatCount(counts.grand)} question bank with real human expert tutors — available for live 1:1
                sessions across every subject we cover.
              </p>
              <ul className="check-list">
                <li>Question bank for daily self-paced practice</li>
                <li>Full review mode — learn from every mistake</li>
                <li>Aira AI chatbot explains every answer in plain language</li>
                <li>Expert tutors for live 1:1 sessions when you need more</li>
                <li>College counseling, ELA, and AP support in the same place</li>
                <li>140+ point SAT improvement guarantee</li>
              </ul>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                <a href="/signup" className="btn-gold">Start Practicing Free</a>
                <a href="/contact" className="btn-outline-navy">Talk to an Expert</a>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="vs-table">
                <thead>
                  <tr>
                    <th style={{ width: '45%' }}>Feature</th>
                    <th className="col-us">Scholar Sphere</th>
                    <th className="col-them">Apps Only</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>SAT + ACT Question Bank</td>
                    <td className="col-us vs-yes">✓ {formatCount(counts.grand)} questions</td>
                    <td className="vs-yes">✓</td>
                  </tr>
                  <tr>
                    <td>Full Session Review Mode</td>
                    <td className="col-us vs-yes">✓</td>
                    <td className="vs-partial">Limited</td>
                  </tr>
                  <tr>
                    <td>Aira AI Chatbot Explanations</td>
                    <td className="col-us vs-yes">✓ Every question</td>
                    <td className="vs-partial">Some</td>
                  </tr>
                  <tr>
                    <td>Real Human Tutors</td>
                    <td className="col-us vs-yes">✓ Live 1:1</td>
                    <td className="vs-no">✗</td>
                  </tr>
                  <tr>
                    <td>College Counseling</td>
                    <td className="col-us vs-yes">✓</td>
                    <td className="vs-no">✗</td>
                  </tr>
                  <tr>
                    <td>Score Guarantee</td>
                    <td className="col-us vs-yes">✓ 140+ pts</td>
                    <td className="vs-partial">Conditional</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Testimonials ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 28 }}>
            <span className="section-label">Student Stories</span>
            <h2 className="section-heading">In their own words.</h2>
          </div>
          <div className="mini-test-grid">
            {testimonials.map((t, i) => (
              <div key={i} className="mini-test-card">
                <div style={{ color: 'var(--gold)', marginBottom: 8 }}>★★★★★</div>
                <p className="mini-test-quote">"{t.quote}"</p>
                <div className="mini-test-name">{t.name} — {t.role}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Final CTA ── */}
        <div className="cta-strip" style={{marginBottom: 32}}>
          <h3>Start for free. Add what you need.</h3>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 15, marginBottom: 24 }}>
            Sign up and access the SAT & ACT question bank in minutes — no credit card required.
            Add live tutoring, AP prep, ELA, or college counseling whenever you're ready.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/signup" className="btn-gold">Start Practicing Free</a>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-outline-white">Book a Free Consultation</a>
          </div>
        </div>

      </div>

      {/* responsive override for offerings grid */}
      <style>{`
        @media (max-width: 900px) {
          .offerings-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 580px) {
          .offerings-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
